/**
 * WaAvatar — círculo de avatar reusable para todo el módulo WhatsApp.
 *
 *   - Si hay `src` y la <img> carga bien → muestra la foto
 *   - Si no hay `src` o la <img> falla → muestra `initials` sobre `bg`
 *
 * El error de carga se trackea con React state (no DOM mutation: cualquier
 * `el.parentElement.textContent = …` confunde la reconciliación cuando
 * cambia el src, y deja la foto vieja pegada). useEffect resetea el flag
 * cada vez que cambia `src` — un nuevo contacto siempre intenta cargar
 * su foto antes de caer a iniciales.
 *
 * El caller computa `initials` y `bg` con los helpers que correspondan
 * (getInitials/getAvatarColor para contactos 1:1, groupParticipantInitials/
 * groupParticipantColor para participantes de grupo). El componente es
 * agnóstico a la paleta.
 */

import { useEffect, useState } from 'react';

interface Props {
  /** Diámetro del círculo en px. */
  size: number;
  /** URL de la foto. Si null/undefined/'' o falla la carga, muestra iniciales. */
  src?: string | null;
  /** Texto de fallback (1-2 letras). */
  initials: string;
  /** Color de fondo (hex/css). Se usa también detrás de la img mientras carga. */
  bg: string;
  /** Override del fontSize de las iniciales. Default: 40% del size. */
  fontSize?: number;
  /** alt de la <img> + aria-label del fallback. */
  alt?: string;
  /** Click → cursor pointer + role=button + Enter handler. */
  onClick?: () => void;
}

export function WaAvatar({ size, src, initials, bg, fontSize, alt = '', onClick }: Props) {
  const [errored, setErrored] = useState(false);
  // Reset al cambiar src — un nuevo contacto debe tratar de cargar su
  // foto antes de caer al fallback de iniciales.
  useEffect(() => { setErrored(false); }, [src]);

  const showImg = !!src && !errored;
  const clickable = !!onClick;
  const commonStyle = {
    width: size, height: size, borderRadius: '50%',
    flexShrink: 0,
    cursor: clickable ? 'pointer' : undefined,
  } as const;

  if (showImg) {
    return (
      <img
        src={src ?? ''}
        alt={alt}
        onError={() => setErrored(true)}
        onClick={onClick}
        style={{
          ...commonStyle,
          objectFit: 'cover',
          background: bg,
        }}
      />
    );
  }

  return (
    <div
      onClick={onClick}
      onKeyDown={clickable ? (e) => { if (e.key === 'Enter') onClick(); } : undefined}
      role={clickable ? 'button' : 'img'}
      aria-label={alt}
      tabIndex={clickable ? 0 : undefined}
      style={{
        ...commonStyle,
        background: bg,
        color: '#fff',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: fontSize ?? Math.round(size * 0.4),
        fontWeight: 600,
        userSelect: 'none',
      }}
    >
      {initials}
    </div>
  );
}
